import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { UserCheck, CheckCircle, ArrowRight } from "lucide-react";

const StrategyService = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <UserCheck className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Strategy & Recruitment</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Develop comprehensive security strategies and find the right talent for your team.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Strategic Security Planning</h2>
              <p className="mb-6">
                Effective cyber security requires more than just technology—it requires a clear strategy and the right people. We help you develop comprehensive security strategies aligned with your business objectives and find the talent needed to execute them, ensuring your security program delivers measurable business value.
              </p>

              <h2 className="text-2xl font-bold mb-4">Strategy Workshop</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>2-Day Executive Workshop:</strong></p>
                <p className="mb-3 text-sm">Collaborative session resulting in documented security strategy aligned with business objectives</p>
                <ul className="space-y-2 text-sm">
                  <li>• Current state assessment and SWOT analysis</li>
                  <li>• Threat landscape review specific to your industry</li>
                  <li>• Risk prioritization aligned with business impact</li>
                  <li>• 3-5 year security roadmap with phased initiatives</li>
                  <li>• Budget planning and investment priorities</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Strategy Deliverables</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>3-5 year security roadmap:</strong> Phased initiatives tied to business milestones</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Annual budget planning:</strong> Detailed cost projections for people, process, and technology</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Technology rationalisation:</strong> Optimise your security tool stack, eliminating redundancy</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Organisational design:</strong> Right-sized security team structure with clear roles</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>KPI framework:</strong> Metrics to measure security program effectiveness</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Recruitment Services</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>Proven placement success:</strong></p>
                <ul className="space-y-2">
                  <li>• <strong>95% retention rate</strong> after 12 months for placed candidates</li>
                  <li>• <strong>Average 4-6 weeks</strong> from role definition to offer accepted</li>
                  <li>• <strong>Roles placed:</strong> Junior analysts to CISOs across all security disciplines</li>
                  <li>• <strong>Technical assessment:</strong> Rigorous evaluation ensuring candidates have claimed skills</li>
                  <li>• <strong>Cultural fit evaluation:</strong> Ensuring candidates align with your organisation's values</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Recruitment Process</h2>
              <div className="space-y-2 mb-6">
                <p><strong>Week 1:</strong> Role definition, salary benchmarking, and sourcing strategy</p>
                <p><strong>Weeks 2-3:</strong> Candidate sourcing, screening, and technical assessment</p>
                <p><strong>Weeks 4-5:</strong> Interviews, references, and cultural fit evaluation</p>
                <p><strong>Week 6:</strong> Offer negotiation and onboarding support</p>
              </div>

              <h2 className="text-2xl font-bold mb-4">Market Intelligence</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Salary benchmarking:</strong> Current market rates for all security roles by region and experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Skills gap analysis:</strong> Identifying capability gaps and training pathways</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Succession planning:</strong> Building resilient teams with knowledge transfer plans</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Training pathways:</strong> Career development plans retaining top talent</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Roles We Recruit</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-secondary/20 rounded-lg p-3">
                  <p className="font-semibold text-sm">Leadership</p>
                  <p className="text-xs text-muted-foreground">CISO, Security Director, Security Manager</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3">
                  <p className="font-semibold text-sm">Operations</p>
                  <p className="text-xs text-muted-foreground">SOC Analysts, Incident Responders, Security Engineers</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3">
                  <p className="font-semibold text-sm">Governance</p>
                  <p className="text-xs text-muted-foreground">GRC Analysts, Risk Managers, Compliance Specialists</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3">
                  <p className="font-semibold text-sm">Technical</p>
                  <p className="text-xs text-muted-foreground">Penetration Testers, Security Architects, DevSecOps</p>
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to develop your security strategy?</h3>
              <p className="text-muted-foreground mb-6">
                Let's create a comprehensive security strategy and build the team to execute it.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StrategyService;
